import { Dialog } from "primereact/dialog";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { useState } from "react";

interface PortfolioItem {
  title: string;
  description: string;
  image: string;
  category: string;
  technologies: string[];
  link?: string;
  detailedDescription?: string;
}

interface PortfolioLightboxProps {
  visible: boolean;
  onHide: () => void;
  category?: string;
}

// Dynamically import all images from the portfolio directory
const importAllImages = () => {
  const images: Record<string, string> = {};
  const imageModules = import.meta.glob(
    "../assets/portfolio/*.{png,jpg,jpeg,svg}",
    { eager: true }
  );

  for (const path in imageModules) {
    const fileName = path.split("/").pop()?.split(".")[0];
    if (fileName) {
      images[fileName] = (imageModules[path] as { default: string }).default;
    }
  }

  return images;
};

const portfolioImages = importAllImages();

const portfolioItems: PortfolioItem[] = [
  {
    title: "ECM Analytics Dashboard",
    description:
      "Real-time analytics and reporting for enterprise content management",
    detailedDescription:
      "Comprehensive analytics dashboard providing insights into document usage, user activity, storage metrics, and system performance. Features customizable charts, exportable reports, and drill-down capabilities for detailed analysis.",
    image: portfolioImages["analytics"],
    category: "Data Visualization",
    technologies: ["React", "TypeScript", "Chart.js", "D3.js", "PrimeReact"],
  },
  {
    title: "Point Rationalization System",
    description: "Intelligent point categorization and organization",
    detailedDescription:
      "Advanced point rationalization interface for managing document classification hierarchies, metadata schemas, and retention policies. Enables administrators to define and maintain complex taxonomies with drag-and-drop simplicity.",
    image: portfolioImages["point-dashboard"],
    category: "Custom Applications",
    technologies: ["React", "TypeScript", "React DnD", "REST API"],
  },
  {
    title: "Tag Management",
    description: "Flexible metadata tagging and classification system",
    detailedDescription:
      "Powerful tag management interface supporting hierarchical tags, auto-tagging rules, and bulk operations. Includes tag analytics, suggestions based on AI/ML, and comprehensive search integration.",
    image: portfolioImages["tag-management"],
    category: "Custom Applications",
    technologies: ["React", "TypeScript", "Elasticsearch", "Machine Learning"],
  },
  {
    title: "Task Management",
    description: "Workflow and task assignment for document processing",
    detailedDescription:
      "Full-featured task management system with assignment workflows, due date tracking, priority management, and notification systems. Supports both manual and automated task creation with customizable templates.",
    image: portfolioImages["task_management_list"],
    category: "Process Automation",
    technologies: ["React", "TypeScript", "WebSocket", "Notification API"],
  },
  {
    title: "System Settings & Configuration",
    description: "Centralized administration and system configuration",
    detailedDescription:
      "Comprehensive settings interface for system-wide configuration, including security policies, storage settings, integration endpoints, and feature toggles. Includes audit logging and role-based access control for sensitive operations.",
    image: portfolioImages["settings-notifications"],
    category: "Custom Applications",
    technologies: ["React", "TypeScript", "RBAC", "Configuration Management"],
  },
  {
    title: "User Management",
    description: "Complete user, role, and permissions administration",
    detailedDescription:
      "Enterprise-grade user management with support for individual users, groups, roles, and granular permissions. Features include bulk user operations, Active Directory integration, session management, and detailed audit trails.",
    image: portfolioImages["settings-account"],
    category: "Custom Applications",
    technologies: ["React", "TypeScript", "Active Directory", "LDAP", "OAuth"],
  },
];

export const PortfolioLightbox: React.FC<PortfolioLightboxProps> = ({
  visible,
  onHide,
  category,
}) => {
  const [selectedItem, setSelectedItem] = useState<number | null>(null);

  const filteredItems = category
    ? portfolioItems.filter((item) => item.category === category)
    : portfolioItems;

  const handleClose = () => {
    setSelectedItem(null);
    onHide();
  };

  return (
    <Dialog
      header={
        selectedItem === null
          ? "Custom Application Portfolio"
          : "Feature Details"
      }
      visible={visible}
      onHide={handleClose}
      style={{ width: "95vw", maxWidth: "1400px" }}
      maximizable
      modal
      className="portfolio-lightbox"
    >
      {selectedItem === null ? (
        // Grid view - Fixed 3 columns per row
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {filteredItems.map((item, index) => (
              <Card
                key={index}
                className="cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col"
                onClick={() => setSelectedItem(index)}
                header={
                  <div
                    className="relative overflow-hidden"
                    style={{ height: "240px" }}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full transition-transform duration-300 hover:scale-110"
                      style={{ objectFit: "cover" }}
                    />
                    <div className="absolute top-2 right-2">
                      <Chip
                        label={item.category}
                        className="bg-blue-600 text-white"
                      />
                    </div>
                  </div>
                }
                title={item.title}
              >
                <div className="flex flex-col h-full">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 flex-grow">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {item.technologies.slice(0, 3).map((tech, i) => (
                      <Chip key={i} label={tech} className="text-xs" />
                    ))}
                    {item.technologies.length > 3 && (
                      <Chip
                        label={`+${item.technologies.length - 3}`}
                        className="text-xs"
                      />
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        // Detail view
        <div className="p-4">
          <Button
            icon="pi pi-arrow-left"
            label="Back to Gallery"
            className="mb-4"
            text
            onClick={() => setSelectedItem(null)}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Image Section */}
            <div className="flex flex-col gap-4">
              <img
                src={filteredItems[selectedItem].image}
                alt={filteredItems[selectedItem].title}
                className="w-full rounded-lg shadow-lg"
                style={{ maxHeight: "600px", objectFit: "contain" }}
              />
              {filteredItems[selectedItem].link && (
                <Button
                  label="Visit Live Project"
                  icon="pi pi-external-link"
                  className="w-full"
                  onClick={() =>
                    window.open(filteredItems[selectedItem].link, "_blank")
                  }
                />
              )}
            </div>

            {/* Details Section */}
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="text-3xl font-bold mb-2">
                  {filteredItems[selectedItem].title}
                </h2>
                <Chip
                  label={filteredItems[selectedItem].category}
                  className="bg-blue-600 text-white mb-4"
                />
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Overview</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {filteredItems[selectedItem].description}
                </p>
              </div>

              {filteredItems[selectedItem].detailedDescription && (
                <div>
                  <h3 className="text-xl font-semibold mb-2">Details</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {filteredItems[selectedItem].detailedDescription}
                  </p>
                </div>
              )}

              <div>
                <h3 className="text-xl font-semibold mb-2">
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {filteredItems[selectedItem].technologies.map((tech, i) => (
                    <Chip
                      key={i}
                      label={tech}
                      icon="pi pi-check-circle"
                      className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};
